import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Phone, ShieldCheck, Activity, ClipboardList, ArrowRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_DISPLAY = "(587) 223-4313";
const PHONE_TEL = "tel:+15872234313";

const IMAGES = {
  service1:
    "https://images.unsplash.com/photo-1579037005241-a79202c7e9fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxhbWJ1bGFuY2UlMjBjb25zdHJ1Y3Rpb258ZW58MHx8fHwxNzkyMzQzODA0fDA&ixlib=rb-4.1.0&q=85",
  service2:
    "https://images.pexels.com/photos/14392249/pexels-photo-14392249.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  service3:
    "https://images.pexels.com/photos/8293670/pexels-photo-8293670.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  band: "https://images.pexels.com/photos/6754159/pexels-photo-6754159.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const SERVICES = [
  {
    spec: "SPEC-01: HEAVY SITE",
    img: IMAGES.service1,
    alt: "Mobile medical treatment truck at active construction site",
    title: "Workplace & Construction Site Standby",
    desc: "Dedicated EMTs stationed at industrial worksites for prompt injury management, emergency medical treatment, and OSHA compliance.",
    tags: ["DAILY / MONTHLY", "ALS EQUIPPED"],
    testid: "service-card-construction",
  },
  {
    spec: "SPEC-02: PUBLIC EVENT",
    img: IMAGES.service2,
    alt: "Emergency medical staging tent at an outdoor event",
    title: "Event Medical Standby",
    desc: "Crowd medical coverage, mobile aid posts, and immediate emergency medical treatment for athletic and corporate events.",
    tags: ["FLEXIBLE DATES", "EMS DISPATCH"],
    testid: "service-card-event",
  },
  {
    spec: "SPEC-03: AUDIT & PLAN",
    img: IMAGES.service3,
    alt: "Safety auditor inspecting industrial medical plans",
    title: "Safety Consulting & Emergency Planning",
    desc: "Site hazard analysis, incident readiness blueprints, and customized emergency action planning.",
    tags: ["CONSULTATION", "COMPLIANCE READY"],
    testid: "service-card-consulting",
  },
];

const MARQUEE_ITEMS = [
  "ON-SITE MEDICAL STANDBY",
  "EMERGENCY MEDICAL TREATMENT",
  "FAST MOBILE RESPONSE",
  "ZERO MISSED SHIFTS",
];

const scrollToQuote = () => {
  document.getElementById("quote-rail")?.scrollIntoView({ behavior: "smooth" });
};

const Nav = () => (
  <nav className="border-b border-slate-800 bg-[#0B0F19]/90 sticky top-0 z-50 backdrop-blur-md" data-testid="site-nav">
    <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
      <div className="flex items-center gap-3" data-testid="nav-brand">
        <div className="w-8 h-8 rounded bg-[#38BDF8]/10 border border-[#38BDF8] flex items-center justify-center">
          <span className="font-mono-ind font-bold text-xs text-[#38BDF8]">EMS</span>
        </div>
        <div>
          <span className="font-poster text-lg text-white block leading-none tracking-wide">PREMIER ON-SITE</span>
          <span className="font-mono-ind text-[9px] text-slate-400 tracking-widest uppercase">
            Mobile Medical Treatment Unit
          </span>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <a
          href={PHONE_TEL}
          className="hidden sm:flex items-center gap-2 font-mono-ind text-xs text-slate-300 hover:text-[#38BDF8] transition-colors"
          data-testid="nav-call-link"
        >
          <Phone className="w-3.5 h-3.5" />
          {PHONE_DISPLAY}
        </a>
        <button
          onClick={scrollToQuote}
          className="px-5 py-2.5 rounded bg-[#38BDF8] hover:bg-[#0284C7] text-slate-950 text-xs font-bold font-mono-ind uppercase tracking-wider transition-colors"
          data-testid="nav-deploy-crew-button"
        >
          Deploy Crew
        </button>
      </div>
    </div>
  </nav>
);

const Hero = () => (
  <section className="py-16 md:py-24 border-b border-slate-800 relative" data-testid="hero-section">
    <div className="max-w-6xl mx-auto px-6">
      <div className="flex items-center gap-2 font-mono-ind text-xs uppercase text-[#38BDF8] mb-6">
        <span className="inline-block w-2 h-2 bg-[#38BDF8] rounded-full animate-ping"></span>
        <span data-testid="hero-eyebrow">Industrial & Construction Emergency Services</span>
      </div>

      <h1
        className="font-poster text-5xl sm:text-7xl lg:text-[96px] leading-[0.92] text-white mb-8"
        data-testid="hero-headline"
      >
        <span className="hero-line">
          <span>Emergency medical treatment on</span>
        </span>
        <span className="hero-line">
          <span className="text-[#38BDF8]">your active jobsite.</span>
        </span>
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
        <div className="lg:col-span-7">
          <p
            className="font-body-ind text-slate-300 text-base sm:text-lg leading-relaxed mb-8"
            data-testid="hero-subline"
          >
            Rapid-response mobile EMS delivering emergency medical treatment, licensed EMTs, advanced field
            trauma kits, and zero missed shifts guaranteed.
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={scrollToQuote}
              className="px-7 py-3.5 rounded bg-[#38BDF8] text-slate-950 font-bold font-mono-ind text-xs uppercase tracking-wider hover:bg-[#0284C7] transition-colors"
              data-testid="hero-schedule-standby-button"
            >
              Schedule Site Standby
            </button>
            <a
              href={PHONE_TEL}
              className="px-7 py-3.5 rounded border border-slate-700 bg-slate-900 text-white font-bold font-mono-ind text-xs uppercase tracking-wider hover:bg-slate-800 transition-colors flex items-center gap-2"
              data-testid="hero-call-dispatch-button"
            >
              <Phone className="w-3.5 h-3.5" />
              Call Dispatch Center
            </a>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div
            className="p-4 rounded-xl border border-slate-800 bg-slate-900/60 font-mono-ind text-xs space-y-2"
            data-testid="hero-protocol-card"
          >
            <div className="text-[#38BDF8] font-bold">OPERATIONAL PROTOCOL</div>
            <div className="text-slate-400">Response Status: Immediate On-Call</div>
            <div className="text-slate-400">Treatment: Emergency Medical Standby</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Marquee = () => (
  <div className="py-4 bg-[#38BDF8] text-slate-950 overflow-hidden font-bold" data-testid="marquee-strip">
    <div className="marquee-track items-center gap-10 text-xs font-mono-ind uppercase tracking-wider">
      {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
        <span key={i} className="flex items-center gap-10">
          <span>★</span>
          <span>{item}</span>
        </span>
      ))}
    </div>
  </div>
);

const Services = () => (
  <section className="py-24 max-w-6xl mx-auto px-6" data-testid="services-section">
    <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-16 gap-4">
      <div>
        <span className="font-mono-ind text-xs uppercase text-[#38BDF8] block mb-2 font-bold">
          Standby Capabilities
        </span>
        <h2 className="font-poster text-3xl sm:text-5xl text-white">Full-Spectrum Field EMS.</h2>
      </div>
      <span className="font-mono-ind text-xs text-slate-400">3 Core Service Divisions</span>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {SERVICES.map((s) => (
        <div
          key={s.spec}
          className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 flex flex-col justify-between hover:border-slate-700 transition-colors"
          data-testid={s.testid}
        >
          <div>
            <div className="w-full h-44 rounded-lg overflow-hidden mb-6 relative">
              <img src={s.img} alt={s.alt} className="w-full h-full object-cover img-drift" loading="lazy" />
              <div className="absolute bottom-3 left-3 bg-black/70 px-2 py-0.5 rounded text-[10px] font-mono-ind text-[#38BDF8]">
                {s.spec}
              </div>
            </div>
            <h3 className="font-poster text-xl text-white mb-2">{s.title}</h3>
            <p className="font-body-ind text-xs text-slate-400 leading-relaxed">{s.desc}</p>
          </div>
          <div className="mt-6 pt-4 border-t border-slate-800 font-mono-ind text-[10px] flex justify-between">
            <span className="text-slate-400">{s.tags[0]}</span>
            <span className="text-[#38BDF8]">{s.tags[1]}</span>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const Differentiator = () => (
  <section className="py-20 border-y border-slate-800 bg-slate-900/60" data-testid="differentiator-section">
    <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-6 space-y-4">
        <span className="font-mono-ind text-xs uppercase text-[#38BDF8] font-bold">
          Why Industry Leaders Choose Us
        </span>
        <h2 className="font-poster text-3xl sm:text-5xl text-white">Direct Medical Safety On Your Site.</h2>
        <p className="font-body-ind text-sm text-slate-300 leading-relaxed" data-testid="differentiator-copy">
          Licensed, experienced EMTs delivering emergency medical treatment. Fast response · we come fully
          equipped to you with reliable coverage and never a missed shift.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
          {[
            { icon: ShieldCheck, label: "Licensed EMTs & Paramedics" },
            { icon: Activity, label: "Fast Response, Fully Equipped" },
            { icon: ClipboardList, label: "Zero Missed Shifts" },
          ].map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-2 p-3 rounded-lg border border-slate-800 bg-slate-900/60"
              data-testid={`differentiator-pill-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <Icon className="w-4 h-4 text-[#38BDF8] shrink-0" />
              <span className="font-mono-ind text-[10px] uppercase text-slate-300 tracking-wide">{label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-6 h-64 rounded-xl overflow-hidden relative border border-slate-800">
        <img
          src={IMAGES.band}
          alt="Mobile emergency life-saving equipment packed in a field vehicle"
          className="w-full h-full object-cover img-drift"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="absolute bottom-5 left-5 right-5 font-mono-ind">
          <span className="text-xs text-[#38BDF8] block font-bold">TURNKEY APPARATUS</span>
          <p className="text-xs text-slate-300">
            Equipped with trauma supplies, oxygen therapy, and treatment kits.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const ReviewsEmpty = () => (
  <section className="py-20 max-w-6xl mx-auto px-6" data-testid="reviews-section">
    <div
      className="p-8 rounded-xl border border-dashed border-slate-800 bg-slate-900/30 text-center"
      data-testid="reviews-empty-state"
    >
      <span className="font-mono-ind text-xs uppercase text-[#38BDF8] block mb-2 font-bold">
        Client Verification
      </span>
      <h3 className="font-poster text-2xl text-white mb-2">Operational Reviews & Field Log</h3>
      <p className="font-body-ind text-xs text-slate-400 max-w-md mx-auto">
        Reviews from project managers and general contractors will appear here after initial scheduled shifts
        are completed.
      </p>
    </div>
  </section>
);

const inputCls =
  "w-full px-4 py-3 rounded bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-[#38BDF8] transition-colors placeholder:text-slate-600";
const labelCls = "block text-xs font-bold font-mono-ind uppercase text-slate-300 mb-1";

const QuoteRail = () => {
  const [form, setForm] = useState({
    contact_name: "",
    company: "",
    contact_method: "",
    location: "",
    dates_required: "",
    service: "Workplace & Construction Site Standby",
    details: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/coverage-requests`, form);
      setSubmitted(true);
      toast.success("Coverage request received. Dispatch will confirm your schedule shortly.");
    } catch (err) {
      toast.error("Request could not be sent. Please call the dispatch line instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote-rail" className="py-24 max-w-6xl mx-auto px-6" data-testid="quote-rail-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-6">
          <span className="font-mono-ind text-xs uppercase text-[#38BDF8] font-bold">Coverage Request</span>
          <h2 className="font-poster text-3xl sm:text-4xl text-white">Deploy on-site medical staff.</h2>
          <p className="font-body-ind text-sm text-slate-400 leading-relaxed">
            Submit jobsite specifications or event dates for immediate schedule confirmation.
          </p>

          <div className="p-5 rounded-xl border border-slate-800 bg-slate-900/80 space-y-3" data-testid="call-direct-box">
            <div className="font-mono-ind text-xs text-[#38BDF8] font-bold uppercase">Direct Phone Line</div>
            <div className="font-mono-ind text-lg text-white font-bold" data-testid="call-direct-number">
              {PHONE_DISPLAY}
            </div>
            <a
              href={PHONE_TEL}
              className="w-full py-3.5 rounded bg-slate-800 hover:bg-slate-700 text-white font-mono-ind text-xs font-bold uppercase tracking-wider transition-colors border border-slate-700 flex items-center justify-center gap-2"
              data-testid="call-coordinator-button"
            >
              <Phone className="w-3.5 h-3.5" />
              Call Medical Coordinator
            </a>
          </div>
        </div>

        <div className="lg:col-span-7 p-8 rounded-xl border border-slate-800 bg-slate-900/50">
          {submitted ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16 space-y-4" data-testid="quote-success-state">
              <div className="w-12 h-12 rounded-full bg-[#38BDF8]/10 border border-[#38BDF8] flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-[#38BDF8]" />
              </div>
              <h3 className="font-poster text-2xl text-white">Request Logged.</h3>
              <p className="font-body-ind text-sm text-slate-400 max-w-sm">
                Your coverage request is in the dispatch queue. A medical coordinator will confirm your
                schedule using the contact details you provided.
              </p>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setForm({
                    contact_name: "",
                    company: "",
                    contact_method: "",
                    location: "",
                    dates_required: "",
                    service: "Workplace & Construction Site Standby",
                    details: "",
                  });
                }}
                className="font-mono-ind text-xs uppercase text-[#38BDF8] hover:text-white transition-colors flex items-center gap-1"
                data-testid="quote-another-request-button"
              >
                Submit another request <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <form className="space-y-4 font-body-ind" onSubmit={handleSubmit} data-testid="coverage-request-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} htmlFor="contact-name">Contact Name</label>
                  <input
                    id="contact-name"
                    type="text"
                    placeholder="Manager Name"
                    className={inputCls}
                    required
                    value={form.contact_name}
                    onChange={update("contact_name")}
                    data-testid="form-contact-name-input"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="company">Company</label>
                  <input
                    id="company"
                    type="text"
                    placeholder="Contractor / Org"
                    className={inputCls}
                    required
                    value={form.company}
                    onChange={update("company")}
                    data-testid="form-company-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} htmlFor="contact-method">Phone or Email</label>
                  <input
                    id="contact-method"
                    type="text"
                    placeholder="How we reach you"
                    className={inputCls}
                    required
                    value={form.contact_method}
                    onChange={update("contact_method")}
                    data-testid="form-contact-method-input"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="location">Location</label>
                  <input
                    id="location"
                    type="text"
                    placeholder="Jobsite or Venue"
                    className={inputCls}
                    required
                    value={form.location}
                    onChange={update("location")}
                    data-testid="form-location-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} htmlFor="dates">Dates Required</label>
                  <input
                    id="dates"
                    type="text"
                    placeholder="Start Date & Duration"
                    className={inputCls}
                    required
                    value={form.dates_required}
                    onChange={update("dates_required")}
                    data-testid="form-dates-input"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="service">Service Division</label>
                  <select
                    id="service"
                    className={inputCls}
                    value={form.service}
                    onChange={update("service")}
                    data-testid="form-service-select"
                  >
                    <option>Workplace & Construction Site Standby</option>
                    <option>Event Medical Standby</option>
                    <option>Safety Consulting & Emergency Action Plan</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="details">Job Details & Crew Count</label>
                <textarea
                  id="details"
                  rows={3}
                  placeholder="Shift hours, specific hazard environment..."
                  className={inputCls}
                  value={form.details}
                  onChange={update("details")}
                  data-testid="form-details-textarea"
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 rounded bg-[#38BDF8] hover:bg-[#0284C7] disabled:opacity-60 text-slate-950 font-bold font-mono-ind text-xs uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
                data-testid="form-submit-button"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? "Sending Request..." : "Send Coverage Request"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="py-12 border-t border-slate-800 bg-[#0B0F19]" data-testid="site-footer">
    <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <span className="font-mono-ind text-xs text-slate-400 uppercase">
          Premier Mobile On-Site EMS Safety Service
        </span>
      </div>
      <span className="font-mono-ind text-[10px] text-slate-600">
        All rights reserved. On-site EMT & Emergency Medical Treatment Coverage.
      </span>
    </div>
  </footer>
);

const Home = () => (
  <div className="bg-[#0B0F19] text-[#F1F5F9] min-h-screen font-body-ind antialiased overflow-x-hidden">
    <Nav />
    <Hero />
    <Marquee />
    <Services />
    <Differentiator />
    <ReviewsEmpty />
    <QuoteRail />
    <Footer />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" theme="dark" />
    </div>
  );
}

export default App;
